import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import { useFeedback } from '../../contexts/FeedbackContext';
import { MailIcon, PhoneIcon, UserIcon } from '../../components/common/Icons';
import { AuthField } from './LoginPage';
import LanguageSelector from '../../components/common/LanguageSelector';
import { TERMS_VERSION } from '../public/TermsPage';
import { formatEmailInput, formatPhoneInput } from '../../utils/inputFormat';
import { requiredFieldMessage, requiredFieldsMessage } from '../../utils/validationMessages';

const RegisterPage = () => {
  const { notify } = useFeedback();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', phone: '', password: '', confirm: '' });
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [fieldErrors, setFieldErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const errors = {};
    const missingLabels = [];
    if (!form.name.trim()) {
      errors.name = requiredFieldMessage(t, t('auth.name'));
      missingLabels.push(t('auth.name'));
    } else if (form.name.trim().length < 2) errors.name = t('auth.validation.name');
    if (!form.email.trim()) {
      errors.email = requiredFieldMessage(t, t('auth.email'));
      missingLabels.push(t('auth.email'));
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errors.email = t('feedback.invalidEmailMessage');
    if (form.phone.trim() && form.phone.trim().length < 6) errors.phone = t('auth.phoneRequired', 'Informe um telefone valido.');
    if (!form.password) {
      errors.password = requiredFieldMessage(t, t('auth.password'));
      missingLabels.push(t('auth.password'));
    } else if (form.password.length < 8) errors.password = t('feedback.passwordInvalidMessage');
    if (!form.confirm) {
      errors.confirm = requiredFieldMessage(t, t('auth.confirmPassword'));
      missingLabels.push(t('auth.confirmPassword'));
    } else if (form.password !== form.confirm) errors.confirm = t('profile.passwordMismatch');
    if (!acceptedTerms) errors.terms = t('terms.acceptRequired');
    return { errors, message: missingLabels.length ? requiredFieldsMessage(t, missingLabels) : Object.values(errors)[0] || '' };
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const { errors, message } = validate();
    if (message) {
      setFieldErrors(errors);
      notify({ variant: 'warning', title: t('feedback.reviewDataTitle'), message });
      return;
    }
    setFieldErrors({});

    setLoading(true);
    try {
      sessionStorage.setItem('clarityPendingRegistration', JSON.stringify({
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim() || null,
        password: form.password,
        language: i18n.language,
        acceptedTerms: true,
        termsVersion: TERMS_VERSION,
      }));
      localStorage.removeItem('clarityRegion');
      navigate('/localidade');
    } catch (err) {
      notify({
        variant: 'danger',
        title: t('feedback.registerErrorTitle'),
        message: err.response?.data?.error || t('feedback.registerErrorMessage'),
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h1 className="text-center text-2xl font-bold text-slate-900">{t('auth.registerTitle')}</h1>
      <p className="mt-2 text-center text-slate-600">{t('auth.subtitle')}</p>
      <LanguageSelector />

      <form onSubmit={handleSubmit} className="mt-8 space-y-5" noValidate>
        <AuthField
          label={`${t('auth.name')} *`}
          value={form.name}
          onChange={(value) => {
            setForm({ ...form, name: value });
            setFieldErrors((current) => ({ ...current, name: '' }));
          }}
          placeholder={t('auth.namePlaceholder', 'Seu nome completo')}
          icon={<UserIcon className="h-5 w-5" />}
          autoComplete="name"
          error={fieldErrors.name}
        />

        <AuthField
          label={`${t('auth.email')} *`}
          type="email"
          value={form.email}
          onChange={(value) => {
            setForm({ ...form, email: formatEmailInput(value) });
            setFieldErrors((current) => ({ ...current, email: '' }));
          }}
          placeholder={t('auth.emailPlaceholder')}
          icon={<MailIcon className="h-5 w-5" />}
          autoComplete="email"
          error={fieldErrors.email}
        />
        <AuthField
          label={t('auth.phone', 'Telefone')}
          type="tel"
          value={form.phone}
          onChange={(value) => {
            setForm({ ...form, phone: formatPhoneInput(value, i18n.language) });
            setFieldErrors((current) => ({ ...current, phone: '' }));
          }}
          placeholder={t('auth.phonePlaceholder', '(11) 99999-0000')}
          icon={<PhoneIcon className="h-5 w-5" />}
          autoComplete="tel"
          error={fieldErrors.phone}
        />
        <AuthField
          label={`${t('auth.password')} *`}
          type="password"
          value={form.password}
          onChange={(value) => {
            setForm({ ...form, password: value });
            setFieldErrors((current) => ({ ...current, password: '' }));
          }}
          placeholder="••••••••"
          icon={<UserIcon className="h-5 w-5" />}
          autoComplete="new-password"
          revealable
          error={fieldErrors.password}
        />
        <AuthField
          label={`${t('auth.confirmPassword')} *`}
          type="password"
          value={form.confirm}
          onChange={(value) => {
            setForm({ ...form, confirm: value });
            setFieldErrors((current) => ({ ...current, confirm: '' }));
          }}
          placeholder={t('auth.passwordPlaceholder')}
          icon={<UserIcon className="h-5 w-5" />}
          autoComplete="new-password"
          revealable
          error={fieldErrors.confirm}
        />

        <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
          <a
            href="/terms"
            target="_blank"
            rel="noreferrer"
            className="font-bold text-sky-600 hover:text-sky-700"
          >
            {t('terms.readLink')}
          </a>
          <label className="mt-3 flex items-start gap-3">
            <input
              className="mt-1 h-4 w-4 rounded border-slate-300 text-sky-500 focus:ring-sky-500"
              type="checkbox"
              checked={acceptedTerms}
              onChange={(event) => {
                setAcceptedTerms(event.target.checked);
                setFieldErrors((current) => ({ ...current, terms: '' }));
              }}
              required
            />
            <span>{t('terms.acceptLabel')} *</span>
          </label>
          {fieldErrors.terms && <p className="mt-2 text-xs font-semibold text-red-600">{fieldErrors.terms}</p>}
        </div>

        <button
          className="inline-flex h-12 w-full items-center justify-center rounded-lg bg-sky-500 px-5 font-bold text-white shadow-soft transition hover:bg-sky-600 disabled:opacity-60"
          type="submit"
          disabled={loading}
        >
          {loading ? t('common.processing') : t('auth.registerButton')}
        </button>
      </form>

      <p className="mt-7 text-center text-sm text-slate-600">
        {t('auth.hasAccount')}{' '}
        <Link to="/login" className="font-bold text-sky-500 hover:text-sky-600">{t('auth.loginButton')}</Link>
      </p>
    </div>
  );
};

export default RegisterPage;
